import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import gamrentLogo from "../../assets/gamrent-logo.jpg";
import api from "../../services/api";
import { resolveAssetUrl } from "../../utils/assetUrl";

const FALLBACK = {
  platformName: "Gamrent",
  tagline: "Property Management Platform",
  email: "info@gamrent.gm",
  phone: "+220 345 6127",
  address: "Opposite Wullinkamma Junction, OIC Road, Sukuta",
  logo: ""
};

const Footer = ({ onLoginClick, onSignupClick }) => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  const [settings, setSettings] = useState(FALLBACK);
  const logoUrl = resolveAssetUrl(settings.logo) || gamrentLogo;

  const navLinks = [
    ["Home", "/"],
    ["Features", "/features"],
    ["How It Works", "/how-it-works"],
    ["Pricing", "/pricing"],
    ["Contact", "/contact"],
  ];

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get("/system-settings/public");
        setSettings({ ...FALLBACK, ...(res.data?.data || {}) });
      } catch (error) {
        setSettings(FALLBACK);
      }
    };

    loadSettings();
  }, []);

  const supportItems = [
    { icon: Mail, label: "Email", value: settings.email },
    { icon: Phone, label: "Phone", value: settings.phone },
    { icon: MapPin, label: "Address", value: settings.address },
  ];

  return (
    <footer className="bg-teal-950 text-teal-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-[max(3.5rem,env(safe-area-inset-bottom))]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-10 border-b border-teal-900">
          <div>
            <button onClick={() => navigate("/")} className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md overflow-hidden flex items-center justify-center border border-teal-700">
                <img src={logoUrl} alt={`${settings.platformName || "Gamrent"} Logo`} className="w-full h-full object-cover" />
              </div>
              <div className="text-left">
                <p className="text-lg font-extrabold text-teal-50">{settings.platformName || "Gamrent"}</p>
              </div>
            </button>
            <p className="text-sm text-teal-200 leading-relaxed max-w-sm">
              Manage properties, tenants, payments, and maintenance from one secure and modern dashboard.
            </p>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-teal-300 mb-4">Navigation</h3>
            <ul className="space-y-3">
              {navLinks.map(([label, path]) => (
                <li key={label}>
                  <button onClick={() => navigate(path)} className="text-sm text-teal-200 hover:text-teal-50 transition-colors">
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-teal-300 mb-4">Support</h3>
            <ul className="space-y-3 text-sm text-teal-200">
              {supportItems.map(({ icon: Icon, label, value }) => (
                <li key={label} className="flex items-start gap-2">
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{label}: {value}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-teal-300 mb-4">Start Today</h3>
            <p className="text-sm text-teal-200 mb-4">Create your account and contact {settings.platformName || "Gamrent"} admin for activation. Use your email to sign in.</p>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              <button onClick={() => (onSignupClick ? onSignupClick() : navigate("/signup"))} className="px-4 py-2.5 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-sm font-bold transition-colors">
                Create Account
              </button>
              <button onClick={() => (onLoginClick ? onLoginClick() : navigate("/login"))} className="px-4 py-2.5 rounded-md border border-teal-500 text-teal-100 text-sm font-bold hover:bg-teal-900 transition-colors">
                Sign In
              </button>
            </div>
          </div>
        </div>

        <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-teal-300">Copyright {year} {settings.platformName || "Gamrent"}. All rights reserved.</p>
          <div className="flex items-center gap-5 text-xs text-teal-300">
            <span>Privacy</span>
            <span>Terms</span>
            <span>Cookies</span>
          </div>
        </div>

        <div className="pt-4 text-center text-xs text-teal-300">
          Built by{" "}
          <a href="https://gamfront.com/" target="_blank" rel="noreferrer" className="font-semibold text-teal-100 hover:text-white underline underline-offset-2">
            Gamfront Technologies
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
